<template>
  <!--
    placement="header": polje u zaglavlju (od 992px naviše)
    placement="page":   polje odmah ispod zaglavlja, iznad dugmadi za deljenje (ispod 992px)
    placement="inline": polje u toku stranice, na svim širinama (npr. iznad naslova "Oglasi na pijaci")
    scope="oglasi":     pretraga samo oglasa; rezultati idu na resultsRoute (npr. oglasi-pretraga)
  -->
  <div class="srch" :class="`srch-${placement}`" ref="root">
    <!-- objasnjenje za pojedinacne pretrage (npr. samo oglasi) -->
    <p v-if="labelText" class="srch-label">{{ labelText }}</p>

    <form class="srch-form" role="search" @submit.prevent="go">
      <div class="srch-field">
        <i class="esei icon-magnifying-glass-solid srch-icon" aria-hidden="true"></i>
        <input ref="input"
               v-model="q"
               type="search"
               class="srch-input"
               :placeholder="placeholder"
               autocomplete="off"
               maxlength="100"
               role="combobox"
               :aria-label="placeholder"
               aria-autocomplete="list"
               :aria-controls="listId"
               :aria-expanded="panelVisible"
               :aria-activedescendant="active >= 0 ? `${listId}-opt-${active}` : null"
               @focus="open = true"
               @keydown="onKeydown" />
        <button v-if="q"
                type="button"
                class="srch-clear"
                aria-label="Obriši pretragu"
                @click="clear">
          <i class="esei icon-close" aria-hidden="true"></i>
        </button>

        <div v-if="panelVisible" :id="listId" class="srch-panel" role="listbox">

          <div v-if="loading && !data" class="srch-msg">Tražim…</div>

          <template v-else-if="data?.groups?.length">
            <div v-for="group in data.groups" :key="group.key" class="srch-group">
              <!-- kad se traži samo jedna grupa, naslov grupe nije potreban -->
              <p v-if="!scope" class="srch-group-title">{{ group.label }}</p>

              <NuxtLink v-for="item in group.items"
                        :key="item.path"
                        :id="`${listId}-opt-${indexOf(item)}`"
                        :to="item.path"
                        class="srch-item"
                        :class="{ 'srch-item-on': indexOf(item) === active }"
                        role="option"
                        :aria-selected="indexOf(item) === active"
                        @click="pick(item)">
                <span class="srch-thumb">
                  <img v-if="item.image" :src="$getImageSrc(item.image)" alt="" loading="lazy" />
                  <span v-else>{{ item.type.charAt(0) }}</span>
                </span>
                <span class="srch-text">
                  <span class="srch-title">{{ item.title }}</span>
                  <span v-if="item.description" class="srch-desc">{{ item.description }}</span>
                </span>
              </NuxtLink>
            </div>

            <NuxtLink :to="allLink" class="srch-all" @click="close">
              Prikaži svih {{ data.total }} {{ noun }} →
            </NuxtLink>
          </template>

          <div v-else class="srch-msg">Nema {{ noun }} za „{{ q.trim() }}“.</div>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import { useSearchStore } from '~/stores/search'

const props = defineProps({
  placement: {
    type: String,
    default: 'header',
    validator: (value) => ['header', 'page', 'inline'].includes(value),
  },
  // null = ceo sajt; 'oglasi' = samo oglasi
  scope: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: 'Pretraži oglase, cene, vesti, recepte…',
  },
  // kratko objasnjenje uz polje; bez zadatog teksta opsta pretraga dobija podrazumevani, a pretraga jedne grupe nista
  // (label="" iskljucuje objasnjenje)
  label: {
    type: String,
    default: null,
  },
  // sta se pretrazuje, u genitivu mnozine ("Prikaži svih 5 oglasa")
  noun: {
    type: String,
    default: 'rezultata',
  },
  // stranica rezultata: ime rute i njena putanja (za prepisivanje upita iz adrese)
  resultsRoute: {
    type: String,
    default: 'pretraga',
  },
  resultsPath: {
    type: String,
    default: '/pretraga',
  },
})

const GLOBAL_LABEL = 'Pretraga celog sajta: oglasi, cene, vesti, recepti, događaji i ostalo.'
const labelText = computed(() => props.label ?? (props.scope ? '' : GLOBAL_LABEL))

const store = useSearchStore()
const route = useRoute()
const router = useRouter()
const routePath = useRoutePath()

const MIN_CHARS = 3

// vise instanci (zaglavlje, stranica, oglasi) ne sme da deli id-jeve
const listId = `srch-list-${props.placement}-${props.scope ?? 'all'}`

const root = ref(null)
const input = ref(null)
const q = ref('')
const open = ref(false)
const loading = ref(false)
const data = ref(null)
const active = ref(-1)

let timer = null
let seq = 0
// kad se upit prepise iz adrese (stranica rezultata), ne treba otvarati listu
let silent = false

const trimmed = computed(() => q.value.trim())

const flat = computed(() => (data.value?.groups ?? []).flatMap((g) => g.items))
const indexOf = (item) => flat.value.indexOf(item)

const panelVisible = computed(() => open.value && trimmed.value.length >= MIN_CHARS && (loading.value || data.value !== null))

const target = (query) => routePath({ name: props.resultsRoute, query })
const allLink = computed(() => target({ q: trimmed.value }))

watch(q, (value) => {
  clearTimeout(timer)
  active.value = -1

  if (silent) {
    silent = false
    return
  }

  const text = value.trim()

  if (text.length < MIN_CHARS) {
    data.value = null
    loading.value = false
    return
  }

  loading.value = true
  open.value = true

  timer = setTimeout(async () => {
    const mine = ++seq
    const res = await store.suggest(text, props.scope)

    // stariji odgovor ne sme da prepise noviji
    if (mine !== seq) return

    const payload = res?.data ?? { q: text, total: 0, groups: [] }

    // pretraga jedne grupe (oglasi): cak i ako API ne poznaje scope, prikazuje se samo ta grupa
    if (props.scope) {
      payload.groups = (payload.groups ?? []).filter((g) => g.key === props.scope)
      payload.total = payload.groups.reduce((n, g) => n + g.items.length, 0) > 0 ? payload.total : 0
    }

    data.value = payload
    loading.value = false
  }, 250)
})

// na stranici rezultata polje pokazuje trenutni upit
const syncFromRoute = () => {
  const fromRoute = route.path.endsWith(props.resultsPath) ? String(route.query.q ?? '') : ''

  if (fromRoute !== q.value) {
    silent = true
    q.value = fromRoute
  }

  data.value = null
  open.value = false
}

watch(() => route.fullPath, syncFromRoute)

const close = () => {
  open.value = false
  active.value = -1
}

// izbor rezultata iz liste: upit i izabrana stranica idu u statistiku pretrage (bez cekanja)
const pick = (item) => {
  store.click(trimmed.value, item.path, 'list', props.scope)
  close()
}

const clear = () => {
  q.value = ''
  data.value = null
  input.value?.focus()
}

const go = () => {
  if (trimmed.value.length < 2) return

  const destination = target({ q: trimmed.value })
  close()
  input.value?.blur()
  router.push(destination)
}

const onKeydown = (e) => {
  if (e.key === 'Escape') {
    close()
    return
  }

  if (!panelVisible.value || flat.value.length === 0) return

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    active.value = (active.value + 1) % flat.value.length
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    active.value = active.value <= 0 ? flat.value.length - 1 : active.value - 1
  } else if (e.key === 'Enter' && active.value >= 0) {
    e.preventDefault()
    const item = flat.value[active.value]
    pick(item)
    router.push(item.path)
  }
}

const onOutside = (e) => {
  if (root.value && !root.value.contains(e.target)) {
    open.value = false
    active.value = -1
  }
}

onMounted(() => {
  syncFromRoute()
  document.addEventListener('mousedown', onOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onOutside)
  clearTimeout(timer)
})
</script>
